/**
 * CareLogix™ ORACLE — Phase 35 AdminOne Executive Intelligence Dashboard.
 *
 * Strictly additive. Reads:
 *   GET /api/oracle/world-mesh/stats
 *   GET /api/oracle/world-mesh/events?limit=20
 *   GET /api/oracle/world-mesh/types
 *   GET /api/oracle/opportunities/stats
 *   GET /api/oracle/opportunities/sources
 *   GET /api/oracle/opportunities/{grants,contracts,donors}?limit=10
 *
 * Truthfulness invariants:
 *   - All "NO_DATA_AVAILABLE" surfaces are shown verbatim.
 *   - No fake counts. No simulated grants.
 *   - Every opportunity displays its source URL and verification timestamp.
 *   - Constitutional compliance footer is always rendered.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

const BACKEND =
  (typeof process !== "undefined" && process.env && process.env.REACT_APP_BACKEND_URL) ||
  "";

const COMPLIANCE = (
  "No guarantee of grant, funding, contract, donor contribution, award, " +
  "investment, partnership, or business outcome. Human review and " +
  "eligibility verification required."
);

const BAND_COLORS = {
  CRITICAL: "#ef4444",
  HIGH:     "#fcd34d",
  MEDIUM:   "#60B8E8",
  LOW:      "#94a3b8",
};

const EVENT_TYPE_COLORS = {
  IPO: "#c5a059", AI_COMPANY: "#c5a059", INVESTMENT: "#c5a059",
  HEALTHCARE: "#60B8E8", BIOTECH: "#60B8E8", PHARMA: "#60B8E8", PUBLIC_HEALTH: "#60B8E8",
  GRANT: "#7AC860", CONTRACT: "#7AC860", DONOR: "#7AC860", FOUNDATION: "#7AC860",
  REGULATORY: "#a78bfa", GOVERNMENT: "#a78bfa",
  GEOPOLITICAL: "#ef4444", COMPETITOR: "#ef4444",
  TECHNOLOGY: "#cbd5e1",
};

function _adminHeaders() {
  const tok =
    (typeof window !== "undefined" && (
      window.localStorage.getItem("carelogix_adminone_token") ||
      window.localStorage.getItem("adminone_jwt") ||
      window.localStorage.getItem("adminone_token")
    )) || "";
  return tok ? { Authorization: `Bearer ${tok}` } : {};
}

async function _get(path) {
  try {
    const r = await fetch(`${BACKEND}${path}`, { headers: { ..._adminHeaders() } });
    if (!r.ok) return null;
    return await r.json();
  } catch { return null; }
}

async function _post(path, body) {
  try {
    const r = await fetch(`${BACKEND}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ..._adminHeaders() },
      body: JSON.stringify(body),
    });
    if (!r.ok) {
      const detail = await r.text();
      return { ok: false, error: detail };
    }
    return await r.json();
  } catch (e) { return { ok: false, error: String(e) }; }
}

const styles = {
  page: {
    minHeight: "100vh",
    padding: "32px 40px 80px",
    background: "linear-gradient(160deg, #050b09 0%, #06231b 60%, #050b09 100%)",
    color: "#dfe3eb",
    fontFamily: "'Figtree', 'Inter', system-ui, sans-serif",
  },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 16, flexWrap: "wrap", marginBottom: 22 },
  eyebrow: { color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.18em", marginBottom: 4 },
  title: { color: "#fde68a", margin: 0, fontSize: 28, fontFamily: "'Cinzel', serif", letterSpacing: "0.02em" },
  sub: { color: "#cbd5e1", marginTop: 8, fontSize: 13, maxWidth: 720, lineHeight: 1.5 },
  link: { color: "#c5a059", textDecoration: "none", fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.1em", marginRight: 18 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14, marginTop: 14, marginBottom: 24 },
  kpi: { background: "rgba(13,26,15,0.92)", border: "1px solid rgba(245,200,66,0.18)", borderRadius: 12, padding: "14px 16px" },
  kpiLabel: { color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.14em" },
  kpiValue: { color: "#fde68a", fontFamily: "'Cinzel', serif", fontSize: 26, marginTop: 6 },
  kpiSub: { color: "#94a3b8", fontSize: 11, marginTop: 4 },
  section: { background: "rgba(13,26,15,0.65)", border: "1px solid rgba(245,200,66,0.20)", borderRadius: 14, padding: 18, marginBottom: 20 },
  sectionTitle: { color: "#fde68a", fontFamily: "'Cinzel', serif", fontSize: 18, margin: 0, marginBottom: 4 },
  sectionSub: { color: "#cbd5e1", fontSize: 12, lineHeight: 1.5, marginBottom: 12, marginTop: 4 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 12, color: "#cbd5e1" },
  th: { textAlign: "left", color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.10em", padding: "8px 8px", borderBottom: "1px solid rgba(245,200,66,0.18)" },
  td: { padding: "8px 8px", borderBottom: "1px dashed rgba(255,255,255,0.05)", verticalAlign: "top" },
  pill: { fontFamily: "'Space Mono', monospace", fontSize: 10, fontWeight: 800, letterSpacing: "0.10em", padding: "2px 9px", border: "1px solid", borderRadius: 999 },
  ingestRow: { display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" },
  input: { flex: 1, minWidth: 200, background: "#040608", border: "1px solid rgba(255,255,255,0.10)", color: "#e2e8f0", padding: "9px 11px", borderRadius: 10, fontSize: 12.5 },
  btn: { appearance: "none", background: "linear-gradient(135deg, #c5a059, #ffd966)", border: "1px solid #c5a059", color: "#0c1a10", padding: "9px 14px", borderRadius: 10, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 800, letterSpacing: "0.06em" },
  btnSecondary: { appearance: "none", background: "transparent", border: "1px solid rgba(255,255,255,0.20)", color: "#cbd5e1", padding: "8px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11 },
  compliance: { marginTop: 28, padding: "14px 18px", background: "rgba(252,211,77,0.06)", border: "1px solid rgba(252,211,77,0.30)", borderRadius: 12, color: "#fde68a", fontSize: 12, lineHeight: 1.5 },
  noData: { color: "#fcd34d", fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.08em" },
};

function KPI({ label, value, sub, testid }) {
  return (
    <div style={styles.kpi} data-testid={testid}>
      <div style={styles.kpiLabel}>{label}</div>
      <div style={styles.kpiValue}>{value}</div>
      {sub ? <div style={styles.kpiSub}>{sub}</div> : null}
    </div>
  );
}

function Pill({ children, color }) {
  return <span style={{ ...styles.pill, color, borderColor: color, background: `${color}1a` }}>{children}</span>;
}

export default function AdminOneExecutiveIntelligence() {
  const [eventStats, setEventStats] = useState(null);
  const [events, setEvents]         = useState([]);
  const [oppStats, setOppStats]     = useState(null);
  const [grants, setGrants]         = useState([]);
  const [contracts, setContracts]   = useState([]);
  const [donors, setDonors]         = useState([]);
  const [sources, setSources]       = useState(null);
  const [loading, setLoading]       = useState(true);
  const [busy, setBusy]             = useState("");
  const [seedHeadline, setSeedHeadline]   = useState("");
  const [seedSummary, setSeedSummary]     = useState("");
  const [seedSourceUrl, setSeedSourceUrl] = useState("");
  const [seedSourceName, setSeedSourceName] = useState("");
  const [lastBrief, setLastBrief] = useState(null);
  const _loadedRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [es, ev, os, sr, gr, ct, dn] = await Promise.all([
      _get("/api/oracle/world-mesh/stats"),
      _get("/api/oracle/world-mesh/events?limit=20"),
      _get("/api/oracle/opportunities/stats"),
      _get("/api/oracle/opportunities/sources"),
      _get("/api/oracle/opportunities/grants?limit=10"),
      _get("/api/oracle/opportunities/contracts?limit=10"),
      _get("/api/oracle/opportunities/donors?limit=10"),
    ]);
    setEventStats(es || null);
    setEvents((ev && ev.events) || []);
    setOppStats(os || null);
    setSources(sr || null);
    setGrants((gr && gr.items) || []);
    setContracts((ct && ct.items) || []);
    setDonors((dn && dn.items) || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (_loadedRef.current) return;
    _loadedRef.current = true;
    const t = setTimeout(() => { load(); }, 0);
    return () => clearTimeout(t);
  }, [load]);

  const seedEvent = useCallback(async () => {
    if (!seedHeadline || seedHeadline.length < 4) return;
    setBusy("seed");
    const out = await _post("/api/oracle/world-mesh/events", {
      headline:    seedHeadline,
      summary:     seedSummary,
      source_url:  seedSourceUrl || undefined,
      source_name: seedSourceName || undefined,
    });
    setBusy("");
    if (out && out.ok) {
      setSeedHeadline(""); setSeedSummary(""); setSeedSourceUrl(""); setSeedSourceName("");
      await load();
    }
  }, [seedHeadline, seedSummary, seedSourceUrl, seedSourceName, load]);

  const runSwarm = useCallback(async (eventId) => {
    setBusy(`swarm:${eventId}`);
    const out = await _post(`/api/oracle/world-mesh/events/${eventId}/process`, {});
    setBusy("");
    if (out && out.brief) setLastBrief(out.brief);
    await load();
  }, [load]);

  const sourcesByTier = useMemo(() => {
    const list = (sources && sources.sources) || [];
    const acc = { Federal: [], State: [], Foundation: [], Corporate: [] };
    list.forEach((s) => { if (acc[s.tier]) acc[s.tier].push(s); });
    return acc;
  }, [sources]);

  return (
    <main style={styles.page} data-testid="adminone-executive-intelligence">
      <header style={styles.header}>
        <div>
          <div style={styles.eyebrow}>PHASE 35 · WORLD KNOWLEDGE MESH · EXECUTIVE INTELLIGENCE</div>
          <h1 style={styles.title}>AdminOne — ORACLE Executive Intelligence</h1>
          <p style={styles.sub}>
            Global Event Command + Opportunity Command Center. Every record is
            truthful: no simulated grants, no fabricated contracts. Sources are
            catalogued and stay <code>STUB_AWAITING_IMPLEMENTATION</code> until
            an env-configured adapter graduates them to <code>LIVE</code>.
          </p>
        </div>
        <div>
          <Link to="/adminone/telemetry"   style={styles.link} data-testid="link-telemetry">← AdminOne Telemetry</Link>
          <Link to="/intelligence-globe"   style={styles.link} data-testid="link-globe">Intelligence Globe →</Link>
        </div>
      </header>

      {/* KPIs */}
      <section data-testid="executive-intel-kpis">
        <div style={styles.grid}>
          <KPI testid="kpi-events-total"     label="Total Events"     value={eventStats?.total_events ?? 0} sub={eventStats?.status || "—"} />
          <KPI testid="kpi-events-verified"  label="Verified Events"  value={eventStats?.verified_events ?? 0} sub="source_verified=true" />
          <KPI testid="kpi-events-critical"  label="Critical / High"  value={`${eventStats?.critical_events ?? 0} / ${eventStats?.high_events ?? 0}`} sub="band: CRITICAL / HIGH" />
          <KPI testid="kpi-briefs"           label="Briefs Generated" value={eventStats?.briefs_generated ?? 0} sub="12-agent swarm" />
          <KPI testid="kpi-grants"           label="Grants (verified)"     value={oppStats?.grant?.count ?? 0}    sub={oppStats?.grant?.status || "—"} />
          <KPI testid="kpi-contracts"        label="Contracts (verified)"  value={oppStats?.contract?.count ?? 0} sub={oppStats?.contract?.status || "—"} />
          <KPI testid="kpi-donors"           label="Donors (verified)"     value={oppStats?.donor?.count ?? 0}    sub={oppStats?.donor?.status || "—"} />
          <KPI testid="kpi-sources-live"     label="Sources LIVE / Catalogued" value={`${oppStats?.sources_live ?? 0} / ${oppStats?.sources_catalogued ?? 0}`} sub="connectors" />
        </div>
      </section>

      {/* Event seeder */}
      <section style={styles.section} data-testid="event-seeder">
        <h2 style={styles.sectionTitle}>Seed a World Mesh Event</h2>
        <p style={styles.sectionSub}>
          AdminOne-only. A verified <code>source_url</code> is required to
          graduate the event to <code>verified</code>. Without one, the event is
          recorded as <code>pending_verification</code> and excluded from
          verified counts.
        </p>
        <div style={styles.ingestRow}>
          <input data-testid="seed-headline" style={styles.input} placeholder="Headline (e.g. 'NIH announces HRSA telehealth grant cycle')" value={seedHeadline} onChange={(e) => setSeedHeadline(e.target.value)} />
          <input data-testid="seed-source-name" style={styles.input} placeholder="Source name (e.g. HRSA)" value={seedSourceName} onChange={(e) => setSeedSourceName(e.target.value)} />
        </div>
        <div style={styles.ingestRow}>
          <input data-testid="seed-source-url" style={styles.input} placeholder="https://… (mandatory for verification)" value={seedSourceUrl} onChange={(e) => setSeedSourceUrl(e.target.value)} />
          <input data-testid="seed-summary" style={styles.input} placeholder="Short summary (optional)" value={seedSummary} onChange={(e) => setSeedSummary(e.target.value)} />
        </div>
        <div style={{ ...styles.ingestRow, marginTop: 12 }}>
          <button data-testid="seed-event-btn" type="button" style={styles.btn} disabled={busy === "seed" || seedHeadline.length < 4} onClick={seedEvent}>
            {busy === "seed" ? "Submitting…" : "Ingest + Classify Event"}
          </button>
          <button type="button" style={styles.btnSecondary} onClick={load}>Refresh</button>
        </div>
      </section>

      {/* Events table */}
      <section style={styles.section} data-testid="events-table-section">
        <h2 style={styles.sectionTitle}>Recent World Mesh Events</h2>
        <p style={styles.sectionSub}>Process an event to run the 12-agent Executive Brief swarm.</p>
        {events.length === 0 ? (
          <div style={styles.noData}>NO_DATA_AVAILABLE — seed an event above to begin.</div>
        ) : (
          <table style={styles.table} data-testid="events-table">
            <thead>
              <tr>
                <th style={styles.th}>Headline</th>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>Band</th>
                <th style={styles.th}>Verified</th>
                <th style={styles.th}>Source</th>
                <th style={styles.th}>Action</th>
              </tr>
            </thead>
            <tbody>
              {events.map((ev) => (
                <tr key={ev._id} data-testid={`event-row-${ev._id.slice(0,8)}`}>
                  <td style={styles.td}>{ev.headline}</td>
                  <td style={styles.td}><Pill color={EVENT_TYPE_COLORS[ev.event_type] || "#cbd5e1"}>{ev.event_type}</Pill></td>
                  <td style={styles.td}><Pill color={BAND_COLORS[(ev.priority || {}).band] || "#94a3b8"}>{(ev.priority || {}).band || "—"}</Pill></td>
                  <td style={styles.td}>{ev.source_verified ? <Pill color="#7AC860">VERIFIED</Pill> : <Pill color="#94a3b8">PENDING</Pill>}</td>
                  <td style={styles.td}>{ev.source_url ? <a href={ev.source_url} target="_blank" rel="noreferrer" style={{ color: "#60B8E8", textDecoration: "underline" }}>{ev.source_name || "link"}</a> : "—"}</td>
                  <td style={styles.td}>
                    <button data-testid={`run-swarm-${ev._id.slice(0,8)}`} type="button" style={styles.btn} disabled={busy === `swarm:${ev._id}`} onClick={() => runSwarm(ev._id)}>
                      {busy === `swarm:${ev._id}` ? "Running…" : ev.brief_id ? "Re-run swarm" : "Run 12-agent swarm"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Last brief */}
      {lastBrief && (
        <section style={styles.section} data-testid="last-brief-section">
          <h2 style={styles.sectionTitle}>Last Executive Brief — {lastBrief.event_type}</h2>
          <p style={styles.sectionSub}>
            Swarm mode: <Pill color={lastBrief.swarm_mode === "live" ? "#7AC860" : "#fcd34d"}>{(lastBrief.swarm_mode || "fallback").toUpperCase()}</Pill>
            {" · "}Verification: <Pill color={lastBrief.verification_status === "verified" ? "#7AC860" : "#94a3b8"}>{lastBrief.verification_status}</Pill>
            {" · "}Priority: <Pill color={BAND_COLORS[lastBrief.priority?.band] || "#94a3b8"}>{lastBrief.priority?.band}</Pill>
          </p>
          <pre style={{ whiteSpace: "pre-wrap", color: "#cbd5e1", fontFamily: "'Figtree', system-ui", fontSize: 12.5, lineHeight: 1.5, margin: 0 }}>
            {lastBrief.narrative}
          </pre>
        </section>
      )}

      {/* Opportunity Command Center — Grants */}
      <OppTable title="Grants" testid="grants-table" items={grants} band={BAND_COLORS} />
      <OppTable title="Contracts" testid="contracts-table" items={contracts} band={BAND_COLORS} />
      <OppTable title="Donors" testid="donors-table" items={donors} band={BAND_COLORS} />

      {/* Sources catalogue */}
      <section style={styles.section} data-testid="sources-catalogue">
        <h2 style={styles.sectionTitle}>Catalogued Sources</h2>
        <p style={styles.sectionSub}>
          All sources are visible. <code>STUB_AWAITING_IMPLEMENTATION</code>
          becomes <code>LIVE</code> when its <code>OPPORTUNITY_FEED_*</code>
          env var is configured. No invented data flows from a STUB source.
        </p>
        {Object.entries(sourcesByTier).map(([tier, rows]) => rows.length > 0 && (
          <div key={tier} style={{ marginBottom: 14 }}>
            <div style={{ ...styles.kpiLabel, marginBottom: 6 }}>{tier}</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {rows.map((s) => (
                <a key={s.key} href={s.url} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }} data-testid={`source-${s.key}`}>
                  <Pill color={s.status === "LIVE" ? "#7AC860" : "#fcd34d"}>{s.name} · {s.kind} · {s.status}</Pill>
                </a>
              ))}
            </div>
          </div>
        ))}
      </section>

      <div style={styles.compliance} data-testid="compliance-footer">
        {COMPLIANCE}
      </div>

      {loading ? <div style={{ ...styles.noData, marginTop: 16 }}>Loading…</div> : null}
    </main>
  );
}

function OppTable({ title, testid, items, band }) {
  return (
    <section style={styles.section} data-testid={`opp-section-${testid}`}>
      <h2 style={styles.sectionTitle}>{title}</h2>
      <p style={styles.sectionSub}>
        Decision-support only. Match scores are not predictions of award.
      </p>
      {items.length === 0 ? (
        <div style={styles.noData}>NO_DATA_AVAILABLE</div>
      ) : (
        <table style={styles.table} data-testid={testid}>
          <thead>
            <tr>
              <th style={styles.th}>Title</th>
              <th style={styles.th}>Award $</th>
              <th style={styles.th}>Deadline (days)</th>
              <th style={styles.th}>Match</th>
              <th style={styles.th}>Source</th>
              <th style={styles.th}>Last Validated</th>
            </tr>
          </thead>
          <tbody>
            {items.map((o) => (
              <tr key={o._id} data-testid={`opp-row-${o._id.slice(0,8)}`}>
                <td style={styles.td}>{o.title}</td>
                <td style={styles.td}>{o.award_size_usd ? `$${Number(o.award_size_usd).toLocaleString()}` : "—"}</td>
                <td style={styles.td}>{o.deadline_days ?? "—"}</td>
                <td style={styles.td}>
                  <Pill color={band[(o.priority_match || {}).band] || "#94a3b8"}>
                    {(o.priority_match || {}).score ?? 0} · {(o.priority_match || {}).band || "—"}
                  </Pill>
                </td>
                <td style={styles.td}>
                  {o.source_url ? <a href={o.source_url} target="_blank" rel="noreferrer" style={{ color: "#60B8E8" }}>{o.source_name || "link"}</a> : "—"}
                </td>
                <td style={styles.td}>{(o.last_validated || "").slice(0, 19)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
